import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router';

import {
  canOpenPredictionForm,
  canOpenStats,
  MatchCard,
  useMatchesStore,
} from '@/entities/match';
import { useDerbyStore } from '@/entities/derby';
import type { DerbyState } from '@/entities/derby';
import { PredictionForm } from '@/features/create-prediction';
import { MatchStatsModal } from './MatchStatsModal';
import { DerbyResultModal } from '@/features/derby-result';
import { usePredictionsStore } from '@/entities/prediction';
import { ROUTES } from '@/shared/config';
import { plural } from '@/shared/lib/format';
import { trackView, VIEW } from '@/shared/lib';
import { OverlayPortal, RADIUS, Skeleton, Squircle } from '@/shared/ui';

import styles from './MatchList.module.scss';

/** Лента матчей, открытых для Прогноза. Тап по карточке открывает шторку. */
export function MatchList() {
  const items = useMatchesStore((state) => state.items);
  const isLoading = useMatchesStore((state) => state.isLoading);
  const error = useMatchesStore((state) => state.error);
  const load = useMatchesStore((state) => state.load);
  const loadPredictions = usePredictionsStore((state) => state.load);
  const derby = useDerbyStore((state) => state.derby);
  const loadDerby = useDerbyStore((state) => state.load);
  const [openMatchId, setOpenMatchId] = useState<string | null>(null);
  const [showDerbyResult, setShowDerbyResult] = useState(false);

  useEffect(() => {
    void load();
    void loadPredictions();
    void loadDerby();
  }, [load, loadPredictions, loadDerby]);

  // Шторка открывается только тому матчу, которому есть что показать.
  // Открываем оверлей только если внутри действительно что-то будет.
  // `PredictionForm` при пустом наборе типов возвращает null, а `.overlay`
  // растянут на весь экран: пустой оверлей ловил бы тапы и блокировал
  // скролл, ничего при этом не показывая и не закрываясь.
  //
  // Условий два, потому что и содержимого два: шторка Прогноза, пока приём
  // открыт, и детали своей ставки, когда матч уже идёт или ждёт расчёта.
  const found = items.find((match) => match.id === openMatchId) ?? null;
  const openMatch =
    found && (canOpenPredictionForm(found) || canOpenStats(found))
      ? found
      : null;

  if (isLoading && items.length === 0) {
    return <Skeleton count={3} height={140} />;
  }

  if (error) {
    return (
      <p className={styles.error} role="alert" data-testid="matches-error">
        {error}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <>
        <Squircle cornerRadius={RADIUS.xl} className={styles.wrap}>
          <p className={styles.empty} data-testid="matches-empty">
            Сегодня прогнозов нет
          </p>
          {derby ? (
            <DerbyPromo
              derby={derby}
              onOpenResult={() => {
                // «Сколько кликов на результаты Дриблов» из ТЗ —
                // только общее количество, то есть неуникальное
                trackView(VIEW.derbyResultClicked);
                setShowDerbyResult(true);
              }}
            />
          ) : null}
        </Squircle>

        {showDerbyResult && derby ? (
          <OverlayPortal>
            <DerbyResultModal
              derby={derby}
              onClose={() => {
                setShowDerbyResult(false);
              }}
            />
          </OverlayPortal>
        ) : null}
      </>
    );
  }

  return (
    <>
      <Squircle cornerRadius={RADIUS.xl} className={styles.wrap}>
        <ul className={styles.list} data-testid="match-list">
          {items.map((match) => (
            <li key={match.id}>
              <MatchCard
                match={match}
                // Тап делает разное в зависимости от того, что сейчас можно.
                // Приём открыт — шторка Прогноза. Приём закрыт, а Прогноз
                // есть (матч идёт или ждёт расчёта) — детали своей ставки.
                //
                // До 09.09.2026 второй случай не открывался вовсе: считалось,
                // что «статистика живёт во вкладке Завершённые». Но туда матч
                // попадает только после публикации итога, а игрок хочет
                // видеть, что поставил, и пока матч идёт.
                onClick={
                  canOpenPredictionForm(match) || canOpenStats(match)
                    ? () => {
                        setOpenMatchId(match.id);
                      }
                    : undefined
                }
              />
            </li>
          ))}
        </ul>
      </Squircle>

      {openMatch ? (
        <OverlayPortal>
          <div className={styles.overlay}>
            {canOpenPredictionForm(openMatch) ? (
              /* Закрывает только крестик — тип не переключается сам после
                 сохранения, игрок выбирает следующий вручную табом. */
              <PredictionForm
                match={openMatch}
                onClose={() => {
                  setOpenMatchId(null);
                }}
              />
            ) : (
              <MatchStatsModal
                match={openMatch}
                onClose={() => {
                  setOpenMatchId(null);
                }}
              />
            )}
          </div>
        </OverlayPortal>
      ) : null}
    </>
  );
}

interface DerbyPromoProps {
  derby: DerbyState;
  onOpenResult: () => void;
}

/**
 * Промо Дерби в пустой ленте Прогнозов (узел 438:51281/438:51292).
 *
 * Пройденное Дерби — отдельное состояние: кнопка меняется на «Результат»
 * и вместо перехода на /derby открывает модалку с итогом (аннотация
 * в макете 438:51870 — «когда Дерби пройдено, кнопка меняется на
 * результат и мы открываем модалку с результатом»).
 */
/**
 * Подпись непройденного Дерби.
 *
 * Формулировка заказчика (01.09.2026); от нас — только числа. Раньше на
 * их месте стояли заглушка «N-очков» и вписанные руками «5 вопросов»,
 * хотя длина Дерби и лесенка наград задаются данными и у каждого Дерби
 * свои. Вписанное в строку число разошлось бы с игрой при первой правке
 * баланса.
 *
 * **Число берётся из `maxPointsPerAnswer`, а не из `maxPoints`.** До
 * 10.09.2026 подставлялось второе — награда за ВСЁ прохождение, — и при
 * лесенке 0→0, 1→20 … 5→100 подпись обещала за каждый ответ сто Очков
 * вместо двадцати. Считает разницу сервер (`reward-ladder.ts`): лесенка
 * живёт в БД и разбирать её на клиенте значило бы завести второй
 * источник правды о наградах.
 *
 * Награды нет — предложение опускаем целиком. Так же и когда лесенка
 * плоская: если Очки дают независимо от числа угаданного, обещать их
 * «за правильный ответ» — неправда. Назвать выдуманную награду хуже,
 * чем не назвать никакой.
 *
 * Каждое предложение — своей строкой (правка 11.09.2026), поэтому
 * отдаётся список строк, а переносы ставит разметка.
 */
function derbyPromoLines(
  questionCount: number,
  maxPointsPerAnswer?: number,
): string[] {
  const intro = 'Отвечай на вопросы и получай очки.';
  const tail = `В дерби ${String(questionCount)} ${plural(questionCount, 'вопрос', 'вопроса', 'вопросов')}`;
  if (maxPointsPerAnswer === undefined) return [intro, tail];
  const points = `${String(maxPointsPerAnswer)} ${plural(maxPointsPerAnswer, 'очко', 'очка', 'очков')}`;
  return [intro, `Каждый правильный ответ – это ${points}.`, tail];
}

function DerbyPromo({ derby, onOpenResult }: DerbyPromoProps) {
  return (
    <Squircle
      cornerRadius={RADIUS.xl}
      className={styles.derbyPromo}
      data-testid="derby-promo"
    >
      <img
        className={styles.derbyTimer}
        src="/images/matches/derby-timer.png"
        alt=""
        aria-hidden
      />
      {/* TODO(design): для пройденного состояния карточки в макете есть
          только текстовая аннотация про смену кнопки на «Результат»
          (438:51870), самого текста заголовка/описания в этом состоянии
          нет — заголовок и подпись ниже придуманы по смыслу. */}
      <p className={styles.derbyTitle}>
        {derby.isFinished ? 'Дерби пройдено' : 'Участвуй в Дерби'}
      </p>
      <p className={styles.derbyText}>
        {derby.isFinished
          ? `Ты ответил правильно на ${String(derby.correctCount)} из ${String(derby.questions.length)} вопросов`
          : derbyPromoLines(
              derby.questions.length,
              derby.maxPointsPerAnswer,
            ).map((line, index) => (
              <Fragment key={line}>
                {index > 0 ? <br /> : null}
                {line}
              </Fragment>
            ))}
      </p>
      {derby.isFinished ? (
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            className={styles.derbyButton}
            onClick={onOpenResult}
            data-testid="derby-promo-result"
          >
            Результат
          </button>
        </Squircle>
      ) : (
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <Link className={styles.derbyButton} to={ROUTES.derby}>
            Поехали
          </Link>
        </Squircle>
      )}
    </Squircle>
  );
}
